import { inject, Injectable } from '@angular/core';

import { ServiceType } from '@cubigo/contracts/lib/enums';

import { ActivityDisplay, ActivityState } from '../../shared/enums';
import { ActivityItem } from '../../shared/interfaces';
import { BuildContextModel } from '../content/models';
import { PresentationContentModel, RuntimeSlideModel } from './models';
import { SlideDurationPolicyService } from './slide-duration-policy.service';
import { ContentKind, SlideLayout } from './enums';

// Overview table, detail slides or both, per the channel's display mode; anything already finished never becomes a slide.
@Injectable({ providedIn: 'root' })
export class ActivitySlideBuilder {
  private readonly duration = inject(SlideDurationPolicyService);

  public build(items: ActivityItem[], context: BuildContextModel): RuntimeSlideModel[] {
    const now = context.clock.now().toEpochMilli();
    const config = context.config;
    const future = items.filter(item =>
      Date.parse(item.occurrenceStartTime) > now && Date.parse(item.occurrenceEndTime) > now);
    const contents = future.map(item => this.contentFor(item, context));

    const details = future.flatMap((item, index) => this.detailFor(item, contents[index], context));

    if (config.raw.channel.activityDisplay === ActivityDisplay.DetailPages || !contents.length) {
      return details;
    }

    const overview = this.overviewFor(contents, future, context);

    return config.raw.channel.activityDisplay === ActivityDisplay.List ? [overview] : [overview, ...details];
  }

  private detailFor(item: ActivityItem, content: PresentationContentModel, context: BuildContextModel): RuntimeSlideModel[] {
    if (item.stateId === ActivityState.Cancelled || item.showDetailsSlide === false) {
      return [];
    }

    return [{
      id: `activities:${item.id}:detail`, kind: ContentKind.Activities, layout: SlideLayout.Split, content,
      duration: this.duration.timingFor(content.bodyText, context.config), images: content.images,
      hasFullscreenContent: false, meta: { sourceId: item.id, expiresAt: Date.parse(item.occurrenceEndTime) }
    }];
  }

  private overviewFor(contents: PresentationContentModel[], items: ActivityItem[], context: BuildContextModel): RuntimeSlideModel {
    return {
      id: 'activities:0:overview', kind: ContentKind.Activities, layout: SlideLayout.Table,
      content: { id: 0, providerChannelIds: [0], activities: contents },
      duration: this.duration.timingFor('', context.config),
      meta: { sourceId: 0, expiresAt: Math.max(...items.map(item => Date.parse(item.occurrenceEndTime))) }
    };
  }

  private contentFor(item: ActivityItem, context: BuildContextModel): PresentationContentModel {
    const { config } = context;
    const formatter = new Intl.DateTimeFormat(config.community.culture, {
      timeZone: config.community.timezone, hour: 'numeric', minute: 'numeric'
    });
    const startTime = formatter.format(Date.parse(item.occurrenceStartTime));
    const endTime = formatter.format(Date.parse(item.occurrenceEndTime));
    const label = config.raw.serviceNames.find(service => service.serviceType === ServiceType.Activities)?.value;

    return {
      id: item.id, title: item.label, subtitle: startTime + (endTime !== startTime ? ` - ${endTime}` : ''),
      label: item.actualLocationLabel ? `${item.actualLocationLabel} - ${label || ''}` : label,
      bodyText: this.duration.shorten(item.shortDescriptionContent?.text || item.fullDescriptionContent?.text || ''),
      providerChannelIds: item.providerChannelIds || [], images: item.picture ? [item.picture] : [],
      startTime, location: item.actualLocationLabel, hasZoom: item.isZoomActivity, hasYoutube: item.isYouTubeActivity,
      stateId: item.stateId, occurenceEndTime: item.occurrenceEndTime, showDetailSlide: item.showDetailsSlide
    };
  }
}
